import { useEffect, useRef, useState } from "react"
import { Page } from "react-pdf"
import type { CellMeta, CellType } from "../api/book"

export interface CellGeom { x: number; y: number; w: number; h: number }

interface BookPageOverlayProps {
  pageIndex: number
  pageNum: number
  width: number
  cells: CellMeta[]
  values: Record<string, string>
  onChange: (cell: CellMeta, value: string) => void
  editMode?: boolean
  edits?: Record<string, CellGeom>
  onCellGeom?: (code: string, geom: CellGeom) => void
  // multi-selection + editor gestures
  selected?: Set<string>
  onSelect?: (code: string, additive: boolean) => void
  onMarquee?: (codes: string[], additive: boolean) => void
  onDragStart?: (code: string) => void
  onMove?: (dx: number, dy: number) => void
  onDragEnd?: () => void
  guides?: { v: number[]; h: number[] }
  labels?: Record<string, string>
}

export function BookPageOverlay({
  pageIndex, pageNum, width, cells, values, onChange,
  editMode = false, edits = {}, onCellGeom, selected, onSelect, onMarquee,
  onDragStart, onMove, onDragEnd, guides, labels = {},
}: BookPageOverlayProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [rendered, setRendered] = useState<{ w: number; h: number } | null>(null)
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null)
  const marq = useRef<{ x0: number; y0: number; additive: boolean } | null>(null)

  useEffect(() => {
    if (!ref.current) return
    const measure = () => {
      const canvas = ref.current?.querySelector("canvas")
      if (canvas) setRendered({ w: canvas.clientWidth, h: canvas.clientHeight })
    }
    measure()
    const t1 = setTimeout(measure, 30)
    const t2 = setTimeout(measure, 200)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [width])

  const geomOf = (c: CellMeta): CellGeom => edits[c.code] ?? { x: c.x, y: c.y, w: c.w, h: c.h }

  // Rubber-band selection on empty PDF area (edit mode only).
  const onRootDown = (e: React.PointerEvent) => {
    if (!editMode || !rendered || !ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rendered.w
    const y = (e.clientY - rect.top) / rendered.h
    marq.current = { x0: x, y0: y, additive: e.shiftKey }
    setMarquee({ x0: x, y0: y, x1: x, y1: y })
    ref.current.setPointerCapture(e.pointerId)
  }
  const onRootMove = (e: React.PointerEvent) => {
    if (!marq.current || !rendered || !ref.current) return
    const rect = ref.current.getBoundingClientRect()
    setMarquee({
      x0: marq.current.x0, y0: marq.current.y0,
      x1: (e.clientX - rect.left) / rendered.w,
      y1: (e.clientY - rect.top) / rendered.h,
    })
  }
  const onRootUp = (e: React.PointerEvent) => {
    if (!marq.current) return
    const m = marquee
    const additive = marq.current.additive
    marq.current = null
    setMarquee(null)
    try { ref.current?.releasePointerCapture(e.pointerId) } catch { /* noop */ }
    if (!m) return
    const lo = { x: Math.min(m.x0, m.x1), y: Math.min(m.y0, m.y1) }
    const hi = { x: Math.max(m.x0, m.x1), y: Math.max(m.y0, m.y1) }
    // ignore tiny drags (treat as click on empty area = clear unless additive)
    if (Math.abs(hi.x - lo.x) < 0.005 && Math.abs(hi.y - lo.y) < 0.005) {
      onMarquee?.([], additive)
      return
    }
    const hit = cells.filter((c) => {
      const g = geomOf(c)
      return g.x < hi.x && g.x + g.w > lo.x && g.y < hi.y && g.y + g.h > lo.y
    }).map((c) => c.code)
    onMarquee?.(hit, additive)
  }

  return (
    <div
      ref={ref}
      style={{ position: "relative", display: "inline-block", boxShadow: "0 6px 24px rgba(0,0,0,0.18)", background: "white" }}
      onPointerDown={onRootDown}
      onPointerMove={onRootMove}
      onPointerUp={onRootUp}
      onPointerCancel={onRootUp}
    >
      <Page pageIndex={pageIndex} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
      {rendered && cells.map((c) => {
        const geom = geomOf(c)
        return editMode ? (
          <EditableCell
            key={c.code}
            geom={geom}
            pageW={rendered.w}
            pageH={rendered.h}
            edited={!!edits[c.code]}
            selected={!!selected?.has(c.code)}
            label={labels[c.code] ?? c.code}
            onSelect={(additive) => onSelect?.(c.code, additive)}
            onDragStart={() => onDragStart?.(c.code)}
            onMove={(dx, dy) => onMove?.(dx, dy)}
            onDragEnd={() => onDragEnd?.()}
            onResize={(g) => onCellGeom?.(c.code, g)}
          />
        ) : (
          <CellInput
            key={c.code}
            cell={c}
            geom={geom}
            pageW={rendered.w}
            pageH={rendered.h}
            value={values[c.code] ?? ""}
            onChange={(v) => onChange(c, v)}
          />
        )
      })}
      {marquee && rendered && (
        <div style={{
          position: "absolute",
          left: Math.min(marquee.x0, marquee.x1) * rendered.w,
          top: Math.min(marquee.y0, marquee.y1) * rendered.h,
          width: Math.abs(marquee.x1 - marquee.x0) * rendered.w,
          height: Math.abs(marquee.y1 - marquee.y0) * rendered.h,
          border: "1px dashed #1565c0", background: "rgba(21,101,192,0.10)",
          pointerEvents: "none",
        }} />
      )}
      {rendered && guides?.v.map((x, i) => (
        <div key={`v${i}`} style={{
          position: "absolute", left: x * rendered.w, top: 0, width: 1, height: rendered.h,
          background: "#e91e63", pointerEvents: "none", zIndex: 5,
        }} />
      ))}
      {rendered && guides?.h.map((y, i) => (
        <div key={`h${i}`} style={{
          position: "absolute", top: y * rendered.h, left: 0, height: 1, width: rendered.w,
          background: "#e91e63", pointerEvents: "none", zIndex: 5,
        }} />
      ))}
      <div style={{
        position: "absolute", bottom: 4, right: 8, fontSize: 10, color: "rgba(0,0,0,0.4)",
        pointerEvents: "none", fontFamily: "ui-monospace, monospace",
      }}>
        p. {pageNum}
      </div>
    </div>
  )
}

// Format the typed value while filling the form.
function sanitize(v: string, type: CellType | undefined, decimals: number | undefined): string {
  if (type === "int") return v.replace(/[^\d]/g, "")
  if (type === "decimal") {
    let s = v.replace(/[^\d.,]/g, "").replace(",", ".")
    const i = s.indexOf(".")
    if (i >= 0) {
      const dec = decimals ?? 2
      s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "").slice(0, dec)
    }
    return s
  }
  return v
}

function CellInput({ cell, geom, pageW, pageH, value, onChange }: {
  cell: CellMeta
  geom: CellGeom
  pageW: number
  pageH: number
  value: string
  onChange: (v: string) => void
}) {
  const h = geom.h * pageH
  const numeric = cell.type === "int" || cell.type === "decimal" || cell.type === undefined
  const style: React.CSSProperties = {
    position: "absolute",
    left: geom.x * pageW,
    top: geom.y * pageH,
    width: geom.w * pageW,
    height: h,
    border: "1px solid rgba(245,197,66,0.8)",
    background: "rgba(245,197,66,0.15)",
    color: "#000",
    fontSize: h * 0.6,
    fontStyle: "italic",
    fontWeight: 400,
    fontFamily: "var(--font)",
    textAlign: "center",
    lineHeight: 1,
    padding: "0 3px",
    boxSizing: "border-box",
    borderRadius: 3,
    outline: "none",
  }
  const dom = [cell.min, cell.max].some((n) => n !== undefined)
    ? `${cell.min ?? "–"}…${cell.max ?? "–"}` : ""
  return (
    <input
      type="text"
      inputMode={numeric ? "decimal" : "text"}
      value={value}
      onChange={(e) => onChange(sanitize(e.target.value, cell.type, cell.decimals))}
      style={style}
      data-cell-code={cell.code}
      title={dom ? `${cell.code} (${dom})` : cell.code}
    />
  )
}

// Draggable + resizable box used in edit mode.
function EditableCell({ label, geom, pageW, pageH, edited, selected, onSelect, onDragStart, onMove, onDragEnd, onResize }: {
  label: string
  geom: CellGeom
  pageW: number
  pageH: number
  edited: boolean
  selected: boolean
  onSelect: (additive: boolean) => void
  onDragStart: () => void
  onMove: (dx: number, dy: number) => void
  onDragEnd: () => void
  onResize: (g: CellGeom) => void
}) {
  const drag = useRef<{ mode: "move" | "resize"; px: number; py: number; start: CellGeom } | null>(null)

  const beginMove = (e: React.PointerEvent) => {
    e.stopPropagation(); e.preventDefault()
    // Keep an existing multi-selection so dragging moves the whole group; only
    // (re)select when this cell isn't already selected.
    if (!selected) onSelect(e.shiftKey)
    onDragStart()
    drag.current = { mode: "move", px: e.clientX, py: e.clientY, start: { ...geom } }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const beginResize = (e: React.PointerEvent) => {
    e.stopPropagation(); e.preventDefault()
    onSelect(false)
    drag.current = { mode: "resize", px: e.clientX, py: e.clientY, start: { ...geom } }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const move = (e: React.PointerEvent) => {
    if (!drag.current) return
    const dx = (e.clientX - drag.current.px) / pageW
    const dy = (e.clientY - drag.current.py) / pageH
    if (drag.current.mode === "move") onMove(dx, dy)
    else onResize({ ...drag.current.start, w: Math.max(0.005, drag.current.start.w + dx), h: Math.max(0.005, drag.current.start.h + dy) })
  }
  const end = (e: React.PointerEvent) => {
    const wasMove = drag.current?.mode === "move"
    drag.current = null
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch { /* noop */ }
    if (wasMove) onDragEnd()
  }

  const color = selected ? "#1565c0" : edited ? "#2e7d32" : "#d12"
  const box: React.CSSProperties = {
    position: "absolute",
    left: geom.x * pageW, top: geom.y * pageH, width: geom.w * pageW, height: geom.h * pageH,
    border: `${selected ? 2 : 1.5}px solid ${color}`,
    background: selected ? "rgba(21,101,192,0.18)" : edited ? "rgba(46,125,50,0.12)" : "rgba(221,17,17,0.10)",
    boxSizing: "border-box", borderRadius: 2, cursor: "move", touchAction: "none",
    zIndex: selected ? 2 : 1,
  }
  return (
    <div style={box} onPointerDown={beginMove} onPointerMove={move} onPointerUp={end} onPointerCancel={end} title={label}>
      <span style={{
        position: "absolute", top: -13, left: 0, fontSize: 8, fontFamily: "ui-monospace, monospace",
        color, whiteSpace: "nowrap", pointerEvents: "none",
      }}>{label}</span>
      <div
        onPointerDown={beginResize} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        style={{ position: "absolute", right: -5, bottom: -5, width: 10, height: 10, background: color, borderRadius: 2, cursor: "nwse-resize", touchAction: "none" }}
      />
    </div>
  )
}
